import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { productOf, useNotifications } from "../lib/notifications";
import { relativeTime } from "../lib/time";
import { cn } from "./ui/cn";

/**
 * Width of the panel, in pixels.
 *
 * Duplicated from the `w-72` class because the clamp below has to do arithmetic
 * with it. Kept as a constant so the number used for the maths and the number
 * used for the layout cannot drift apart.
 */
const PANEL_WIDTH = 288;

/**
 * The dot that says which product a row came from.
 *
 * Three sources share one list now, and the times alone do not tell you whether
 * "Jade logged a call with NetXL" is something you were mentioned in or
 * something you follow.
 */
const DOT: Record<string, string> = {
  helpdesk: "bg-sla-ok",
  crm: "bg-sla-warning",
  sales: "bg-brand-600",
  meetings: "bg-brand-400",
};

/** Space between the bell and the panel, and between the panel and the edge. */
const GAP = 6;
const MARGIN = 8;

/**
 * Recent notifications, behind a bell.
 *
 * The only in-app surface notifications have. There used to be a toast as
 * well, and it was in the way: a card over the ticket somebody was reading, for
 * news the badge was already carrying. Now an arrival moves the count and
 * nothing else, and the list is read here when the reader chooses to.
 *
 * `tone` exists because this sits on two different backgrounds -- the pale
 * sidebar on a desktop, and the brand-pink bar on a phone -- and white-on-pale
 * is as unreadable as dark-on-pink.
 *
 * The panel is portalled to the body and positioned by hand rather than being
 * an absolutely-positioned child. It has to be: the sidebar it sits in is
 * `overflow-y-auto`, and a scroll container clips on *both* axes -- CSS
 * promotes the other axis to `auto` as soon as one of them is not `visible`, so
 * there is no way to overflow a 288px panel out of a 240px column from inside
 * it. Anchoring to the trigger's viewport rect sidesteps the clip entirely and
 * lets the same component work in the narrow sidebar and the phone header.
 */
export function NotificationBell({ tone = "light" }: { tone?: "light" | "brand" }) {
  const { items, unreadCount, markAllRead, clear } = useNotifications();
  const [open, setOpen] = useState(false);
  /*
   * Which notifications were unread at the moment the panel was opened.
   *
   * Opening marks everything read, so without this snapshot the panel would
   * always render as a flat list of things already seen -- the badge would go
   * from a number to nothing with no indication of which rows it had been
   * counting. These are the rows it was counting.
   */
  const [fresh, setFresh] = useState<Set<number>>(new Set());
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /*
   * Prefer the panel's left edge under the bell's, and slide it back on screen
   * when there is not room. In the sidebar that opens rightwards over the
   * content; in the phone header, where the bell is hard against the right
   * edge, the clamp lands it flush to that edge instead. One rule, both cases,
   * no per-caller alignment prop to get wrong.
   */
  const place = useCallback(() => {
    const trigger = box.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    setAnchor({
      top: rect.bottom + GAP,
      left: Math.max(
        MARGIN,
        Math.min(rect.left, window.innerWidth - PANEL_WIDTH - MARGIN),
      ),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    const onDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      // Two containers to check now that the panel is not a descendant of the
      // trigger -- missing the panel would close it on its own scrollbar.
      if (box.current?.contains(target) || panel.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };

    /*
     * Capture, so scrolling the sidebar the bell lives in is seen. Scrolling
     * inside the panel's own list fires this too and simply recomputes the same
     * rect, which is why this repositions rather than closing.
     */
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          // Opening is reading. A badge that survived being looked at would
          // just be a number nobody trusts.
          if (next) {
            setFresh(
              new Set(items.filter((item) => !item.read).map((item) => item.id)),
            );
            markAllRead();
          }
        }}
        aria-label={
          unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"
        }
        aria-expanded={open}
        className={cn(
          /* Thumb-sized in the phone's pink bar, where it is one of four
             controls rather than one of a sidebar's dozen. */
          "relative flex shrink-0 items-center justify-center rounded-md",
          tone === "brand" ? "size-9" : "size-6",
          "focus:outline-none focus-visible:ring-2",
          tone === "brand"
            ? "text-white hover:bg-white/15 focus-visible:ring-white/60"
            : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400 focus-visible:ring-brand-500/40",
        )}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>

        {unreadCount > 0 ? (
          <span
            aria-hidden
            className={cn(
              "absolute flex min-w-3.5 items-center justify-center",
              "rounded-full px-0.5 text-[9px] font-semibold leading-3.5",
              /* On the bell's shoulder either way: the phone's larger box
                 would otherwise carry the count off into its corner. */
              tone === "brand"
                ? "right-1.5 top-1.5 bg-white text-brand-700"
                : "-right-0.5 -top-0.5 bg-brand-600 text-white",
            )}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open && anchor
        ? createPortal(
            <div
              ref={panel}
              role="dialog"
              aria-label="Notifications"
              style={{ top: anchor.top, left: anchor.left, width: PANEL_WIDTH }}
              className={cn(
                "fixed z-50 overflow-hidden rounded-lg",
                "border border-line bg-surface shadow-xl",
              )}
            >
              <header className="flex items-center gap-2 border-b border-line px-3 py-2">
                <span className="text-xs font-medium text-content">
                  Notifications
                </span>
                {/*
                  The number the badge was showing, kept where it can still be
                  read after opening has cleared the badge.
                */}
                {fresh.size > 0 ? (
                  <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-semibold leading-4 text-white">
                    {fresh.size} new
                  </span>
                ) : null}
                {/*
                  The way out to the full history, and it is in the header
                  rather than at the foot of the list on purpose: at the bottom
                  it would sit below fifty rows, which is exactly where somebody
                  who cannot find an old notification will never scroll to.

                  Always offered, even with nothing in the panel. An empty bell
                  is one of the moments somebody most wants to go and look
                  properly.
                */}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/notifications");
                  }}
                  className="ml-auto text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
                >
                  History
                </button>

                {items.length > 0 ? (
                  <button
                    type="button"
                    onClick={clear}
                    className="text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    Clear
                  </button>
                ) : null}
              </header>

              {items.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-faint">
                  Nothing yet.
                </p>
              ) : (
                <ul className="max-h-80 overflow-y-auto">
                  {items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          navigate(item.href);
                        }}
                        className={cn(
                          "flex w-full items-start gap-2 border-b border-line px-3 py-2 text-left last:border-0 hover:bg-surface-2",
                          // Tinted, so the new ones are picked out of the list
                          // rather than having to be worked out from the times.
                          fresh.has(item.id) && "bg-brand-50 dark:bg-brand-950/40",
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "mt-1.5 size-1.5 shrink-0 rounded-full",
                            DOT[productOf(item.eventKey)],
                            // Already-seen rows keep the dot for kind, but stop
                            // competing with the ones that are new.
                            !fresh.has(item.id) && "opacity-40",
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-medium text-content">
                            {item.title}
                            {fresh.has(item.id) ? (
                              <span className="sr-only"> (new)</span>
                            ) : null}
                          </span>
                          <span className="block truncate text-xs text-muted">
                            {item.body}
                          </span>
                          <span className="block text-[10px] text-faint">
                            {relativeTime(item.at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
